import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ArrowRight, X } from 'lucide-react';

export const ContinueReading = ({ articles, onDismiss }) => {
  if (!articles || articles.length === 0) return null;

  const timeAgo = (ts) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="mb-12" data-testid="continue-reading-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#00F0FF]" />
          <h2 className="text-xl text-white font-heading uppercase tracking-wider">Continue Reading</h2>
          <span className="text-[10px] text-zinc-600 uppercase tracking-widest ml-2 hidden sm:block">Pick up where you left off</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.slice(0, 6).map(article => (
          <Link
            key={article.slug}
            to={`/blogs/article/${article.slug}`}
            className="group relative bg-zinc-900/50 border border-zinc-800 hover:border-[#00F0FF]/30 transition-all duration-300 overflow-hidden"
            data-testid={`continue-reading-${article.slug}`}
          >
            {onDismiss && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(article.slug); }}
                className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center bg-black/60 text-zinc-500 hover:text-white hover:bg-[#E63946]/60 transition-colors"
                data-testid={`dismiss-${article.slug}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
            <div className="relative h-32 overflow-hidden">
              {article.featured_image ? (
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-zinc-800" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              {/* Progress bar overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800/80">
                <div
                  className="h-full bg-gradient-to-r from-[#E63946] to-[#00F0FF] transition-all"
                  style={{ width: `${article.progress}%` }}
                  data-testid={`progress-indicator-${article.slug}`}
                />
              </div>
              <span className="absolute top-2 left-2 px-2 py-0.5 text-[9px] uppercase tracking-wider bg-[#E63946] text-white font-bold">
                {article.category}
              </span>
            </div>
            <div className="p-4">
              <h3 className="text-white text-sm font-heading leading-tight group-hover:text-[#00F0FF] transition-colors line-clamp-2 mb-2">
                {article.title}
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.read_time}m
                  </span>
                  <span>{Math.round(article.progress)}% read</span>
                </div>
                <span className="text-zinc-600 text-[10px]">{timeAgo(article.lastRead)}</span>
              </div>
              <div className="mt-2 text-[#00F0FF] text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Continue <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ContinueReading;
